import React, { useEffect, useMemo, useState } from "react";
import { FormikProvider, useFormik } from "formik";
import { BannerProps, Select, NumberInput, Tooltip, TooltipEnums, Spinner } from "kubra-ux-forge";
import { useTranslation } from "react-i18next";
import { ORDER_NAMESPACE, COMMON_NAMESPACE, VALIDATE_NAMESPACE } from "constants/i18n-namespaces";
import { useNavigate } from "react-router";
import { getDomain, getLabelWithKrn } from "utilities/helper";
import { useLabelsWithKrn } from "hooks";
import {
  postPayableCreate,
  postPayableInitiateIntent,
} from "api/IamApi/entities/ezpay";
import routing from "routing";
import { useDispatch, useSelector } from "react-redux";
import IamApi from "api/IamApi";
import { OrderPayable, PayableType, IntegerField } from "types/payable";
import { setPayableSession } from "actions/payableSessionActions";
import { getPayableSession } from "selectors/payableSession";
import { IContactInfo } from "types/biller";
import { getDeviceFingerprint } from "selectors/appState";
import { generateValidationSchema } from "components/extension-fields/schema";
import ContinueButton from "../continue-button/continue-button";

export interface OrderFormProps {
  setBannerState: (state: BannerProps) => void;
  biller: any;
  selectedPayableType: PayableType;
  selectedBusinessUnitError: boolean;
  setSelectedBusinessUnitError: (isError: boolean) => void;
}

export const OrderForm: React.FC<OrderFormProps> = (props) => {

  const { setBannerState, biller } = props;
  const fingerprint = useSelector(getDeviceFingerprint);
  const [loading, setLoading] = useState(false);
  const [showSpinner, setShowSpinner] = useState(false);
  const payableSession = useSelector(getPayableSession);
  const payableFromSession = payableSession?.payable as OrderPayable | undefined;

  const selectedPayableType = props.selectedPayableType;

  const navigate = useNavigate();
  const { t, i18n } = useTranslation([ORDER_NAMESPACE]);

  const { getLabelsWithKrn } = useLabelsWithKrn();
  const labelSourceWithKrn = getLabelsWithKrn();
  const domain = getDomain();
  const dispatch = useDispatch();

  const { name: payableTypeName, supportedBusinessUnitCodes, typeKrn, maxQuantity, unitPrice } = selectedPayableType;

  const fieldsForOrder: IntegerField[] = [
    { type: "Integer", name: "payableQuantity", min: 1, max: maxQuantity ?? 1 },
  ];

  const contactUs = biller?.properties?.find((element: any) => {
    return element.name === "InstanceConfiguration";
  }) as IContactInfo;

  const isMoreThanOneBusinessUnitSupported = supportedBusinessUnitCodes && supportedBusinessUnitCodes.length > 1

  const businessUnitCodes = useMemo(() => {
    if (!supportedBusinessUnitCodes || supportedBusinessUnitCodes.length === 0) {
      return [];
    }

    return supportedBusinessUnitCodes.map((code) => {
      if (typeof code === "string") {
        return {
          name: getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, code),
          value: code,
        };
      }
    });
  }, [supportedBusinessUnitCodes, labelSourceWithKrn, typeKrn]);

  const submitOrderPayable = async (typeKrn: string, payableFields: unknown) => {
    setLoading(true);
    try {

      const lookupResponse = await postPayableCreate(typeKrn, payableFields);
      const payable = lookupResponse.data as PayableType;

      let nextPage: string | undefined = undefined;
      if (
        selectedPayableType?.extensionFields?.length > 0
      ) {
        nextPage = routing.payableDetails.index();
      } else {
        // If no extension fields, then initiate the payment intent.
        await postPayableInitiateIntent(
          payable.krn,
          fingerprint,
          navigator.userAgent
        );
        nextPage = routing.payableDashboard.index();
      }

      const updatedSession = await IamApi.ezpay.getPayableFromSession(domain);
      dispatch(setPayableSession(updatedSession));

      navigate(nextPage);

    } catch (error: any) {

      if (
        error.errorMessageCode === 404 &&
        selectedPayableType?.category?.toLowerCase() === "order"
      ) {
        const messageLabel = t("invalid-entry", { ns: VALIDATE_NAMESPACE })
        const messageBody = t("validation-error", {
          billedName: biller?.billerName,
          contactNo: contactUs.properties.phone,
        });

        setBannerState({
          active: true,
          variant: "error",
          messageLabel,
          messageBody,
          withClose: true,
          withIcon: true,
          withHyperlink: false,
        });
      }


    } finally {
      setLoading(false);
      setShowSpinner(false);
    }
  }

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      payableQuantity: payableFromSession?.quantity ?? 1,
      selectedBusinessUnit: payableFromSession?.businessUnitCode ?? "",
    },
    validationSchema: generateValidationSchema(fieldsForOrder, t, labelSourceWithKrn.labelsWithKrn, typeKrn),
    onSubmit: (values) => {
      const { payableQuantity } = values;
      const payableFields = {
        spaceKrn: biller.spaceKrn,
        quantity: Number(payableQuantity),
        businessUnitCode: formik.values.selectedBusinessUnit,
        payableAmount: Number(unitPrice) * Number(payableQuantity),
        domain: domain,
        channel: "Web",
        language: i18n.language,
      };

      submitOrderPayable(typeKrn, payableFields);
    }
  });

  const isMinQuantity = Number(formik.values.payableQuantity) <= 1;

  useEffect(() => {
    formik.validateForm();
    formik.setTouched({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  useEffect(() => {
    if (supportedBusinessUnitCodes && !isMoreThanOneBusinessUnitSupported) {
      formik.setFieldValue("selectedBusinessUnit", supportedBusinessUnitCodes[0]);

      // Auto-advance: with exactly one business unit and max quantity = 1, there is nothing for the user to choose,
      // so immediately create the payable and move to the next step (details if extensions exist, otherwise payment/dashboard).
      if (maxQuantity === 1) {
        setShowSpinner(true);
        const payableFields = {
          spaceKrn: biller.spaceKrn,
          quantity: 1,
          businessUnitCode: supportedBusinessUnitCodes[0],
          payableAmount: Number(unitPrice),
          domain: domain,
          channel: "Web",
          language: i18n.language,
        };

        submitOrderPayable(typeKrn, payableFields);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supportedBusinessUnitCodes]);

  useEffect(() => {
    if (!quantityWrapperRef.current) return;
    const decBtn = quantityWrapperRef.current.querySelector('button[aria-label="subtract 1 from value"]');
    if (decBtn) {
      if (isMinQuantity) {
        decBtn.setAttribute("disabled", "true");
        decBtn.setAttribute("aria-disabled", "true");
      } else {
        decBtn.removeAttribute("disabled");
        decBtn.removeAttribute("aria-disabled");
      }
    }
  }, [isMinQuantity]);


  const handleContinueClick = () => {
    if (isMoreThanOneBusinessUnitSupported && !formik.values.selectedBusinessUnit) {
      props.setSelectedBusinessUnitError(true);
      return;
    }
    props.setSelectedBusinessUnitError(false);
    formik.handleSubmit();
  };

  const hasNumberError = formik.touched.payableQuantity && !!formik.errors.payableQuantity
  const quantityWrapperRef = React.useRef<HTMLDivElement | null>(null);

  if (showSpinner) {
    return (
      <div className="order-form-spinner">
        <Spinner
          size="large"
          variant="primary"
        />
      </div>
    )
  }

  return (
    <div className="order-form">
      <FormikProvider value={formik}>
        <form
          onSubmit={formik.handleSubmit}
          key={i18n.language}
          data-testid="payable-amount-form"
          className="form-container"
        >
          {isMoreThanOneBusinessUnitSupported && (
            <div className="ez-pay-payable-row">
              <label
                htmlFor={`select${payableTypeName}`}
                className="validate-label"
              >
                {getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, 'businessUnitTypeSelection')}
              </label>
              <Select
                name="selectedBusinessUnit"
                label={""}
                onChange={(option: { id: string; image: string }[]) => {
                  formik.setFieldValue("selectedBusinessUnit", option[0].id);
                  props.setSelectedBusinessUnitError(false);
                }}
                defaultValue={formik.values.selectedBusinessUnit}
                placeholder={t("select", { ns: COMMON_NAMESPACE })}
                options={businessUnitCodes.map((code) => ({
                  id: code?.value,
                  name: getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, code?.name),
                }))}
                hasError={
                  formik.touched.selectedBusinessUnit && formik.values.selectedBusinessUnit === ''
                }
                canDeselect={false}
              />
            </div>
          )}
          {
            maxQuantity && maxQuantity > 1 && (
              <>
                <div
                  ref={quantityWrapperRef}
                  className={`ez-pay-payable-row quantity-selector${hasNumberError ? " input-error" : ""}`}
                  data-min-reached={isMinQuantity ? "true" : "false"}
                >
                  { !isMoreThanOneBusinessUnitSupported && <p className="quantity-selection-label">{getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, 'quantitySelectionLabel')}</p>}
                  <NumberInput
                    label={getLabelWithKrn(labelSourceWithKrn.labelsWithKrn, typeKrn, 'payableQuantity')}
                    name="payableQuantity"
                    aria-required="true"
                    dataCy="payable-quantity"
                    defaultValue={formik.values.payableQuantity}
                    value={formik.values.payableQuantity}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      formik.setFieldValue("payableQuantity", e.target.value);
                      formik.setTouched({ ...formik.touched, payableQuantity: true }, false);
                      formik.validateField("payableQuantity"); // validate on every change
                    }}
                    onBlur={formik.handleBlur}
                    errorText={formik.errors.payableQuantity}
                    aria-describedby={hasNumberError ? "payable-quantity-error" : undefined}
                  />
                  {
                    hasNumberError && (
                      <p className="input-error-text" id="payable-quantity-error" data-testid="payable-quantity-error">
                        {formik.errors.payableQuantity}
                      </p>
                    )
                  }
                </div>
                {/*
                  // Show tooltip always; if quantity invalid (> max or has error) show placeholder "__" instead of computed total.
                */}
                <div className="order-payable-tooltip">
                  <Tooltip
                    direction={TooltipEnums.Bottom}
                    content={t("additional-service-fee")}
                    data-cy="estimated-amount-due-tooltip"
                    variant="rich"
                  />
                  {" "}{`${t("estimated-total", { ns: ORDER_NAMESPACE })}: ${(
                    hasNumberError || Number(formik.values.payableQuantity) > maxQuantity
                      ? "__"
                      : `$${(Number(unitPrice) * Number(formik.values.payableQuantity)).toFixed(2)}`
                  )}`}
                </div>
              </>
            )
          }
          <ContinueButton loading={loading} onClick={() => handleContinueClick()} />
        </form>
      </FormikProvider>
    </div>
  );
};

export default OrderForm;
